import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import StudentDashboard from "./pages/StudentDashboard";
import TrainingPage from "./pages/TrainingPage";
import ExamPage from "./pages/ExamPage";
import ExamReviewPage from "./pages/ExamReviewPage";
import InstructorExamReviewPage from "./pages/InstructorExamReviewPage";
import BadgesPage from "./pages/BadgesPage";
import ReservationsPage from "./pages/ReservationsPage";
import PilotFlightLogPage from "./pages/PilotFlightLogPage";
import MeusDocumentosPage from "./pages/MeusDocumentosPage";
import MissionsPage from "./pages/MissionsPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import InstructorDashboard from "./pages/InstructorDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import SuperAdminSchools from "./pages/admin/SuperAdminSchools";
import SubscriptionPage from "./pages/SubscriptionPage";
import PublicInstructorStatus from "./pages/PublicInstructorStatus";
import HelpPage, { HelpIndex } from "./pages/HelpPage";
import { MANUALS } from "./help";
import { resolveHomePath } from "./utils/roleRedirect";
import { isFleetOnly, FLEET_HOME } from "./config/productConfig";

// Storage key for session token (iOS Safari fallback)
export const SESSION_TOKEN_KEY = 'utc_session_token';

// One-time cleanup of stale localStorage tokens on app load
// This handles the migration from the old system that was causing conflicts
const TOKEN_CLEANUP_KEY = 'utc_token_cleanup_v1';
try {
  if (typeof window !== 'undefined' && !sessionStorage.getItem(TOKEN_CLEANUP_KEY)) {
    // Check if we have a stored token but no indication it was set in current session
    const storedToken = localStorage.getItem(SESSION_TOKEN_KEY);
    if (storedToken) {
      // Token exists from before - it might be stale
      // We'll let checkAuth handle validation, but mark that we've done the check
      console.log('[Auth] Found existing localStorage token, will validate on checkAuth');
    }
    sessionStorage.setItem(TOKEN_CLEANUP_KEY, 'done');
  }
} catch (e) {
  // Storage not available
}

// Backend URL configuration
// In production with custom domain, REACT_APP_BACKEND_URL should be updated by Emergent
// Fallback to window.location.origin for any hostname that isn't the
// canonical training domain (preview, custom domains, proxied domains).
const CANONICAL_TRAINING_HOSTS = ['utc.com.pt', 'www.utc.com.pt'];

const getBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isPreviewHost = hostname.includes('preview.emergentagent.com');
    const isCanonicalTrainingHost = CANONICAL_TRAINING_HOSTS.includes(hostname);

    // Any non-canonical hostname (a fleet-product domain fronted by the
    // Cloudflare proxy, or an ad-hoc test domain) must talk to the API on
    // its own origin — the session cookie is host-only (no Domain=
    // attribute), so it's only sent back on requests to the same host the
    // browser actually connected to. Calling utc.com.pt's API directly from
    // a different domain would also leak the training domain in devtools.
    if (!isPreviewHost && !isCanonicalTrainingHost) {
      console.log('[API] Using window.location.origin (non-canonical hostname)');
      return window.location.origin;
    }
  }

  return envUrl;
};

const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Configure axios interceptor to ALWAYS add Authorization header when we have a token
// This is essential for iOS Safari where cookies don't work reliably
// The backend gives priority to cookies, so this is safe even when cookies work
axios.interceptors.request.use(
  (config) => {
    // Only add token for requests to our API
    if (config.url?.includes('/api/')) {
      try {
        const storedToken = localStorage.getItem(SESSION_TOKEN_KEY);
        if (storedToken) {
          // Always add Authorization header - backend will use cookie if available (has priority)
          // This ensures iOS Safari works where cookies don't persist
          config.headers = config.headers || {};
          config.headers['Authorization'] = `Bearer ${storedToken}`;
        }
      } catch (e) {
        // localStorage might not be available
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth failures
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // If we get 401 on an authenticated endpoint, the token might be stale
    // But we don't clear it here - let checkAuth handle that logic
    return Promise.reject(error);
  }
);

// Auth context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, trainingOnly }) => {
  const { user, loading, school } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 font-mono">A carregar...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Fleet-only (fleet domain or fleet school) has no training area: admins go to /admin, pilots and
  // instructors to /reservas. Neither target is trainingOnly, so there is no redirect loop.
  if (trainingOnly && isFleetOnly(school)) {
    const fleetHome = user.role === "admin" || user.role === "super_admin" ? "/admin" : FLEET_HOME;
    return <Navigate to={fleetHome} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Super-admin bypasses all role checks (cross-tenant operator)
    if (user.role === "super_admin") return children;
    return <Navigate to={resolveHomePath(user)} replace />;
  }

  return children;
};

// Help manuals: logged-in users only, and only the roles the manual is for (unknown slug: any user, the page redirects).
const HelpGate = ({ children }) => {
  const { manual } = useParams();
  return <ProtectedRoute allowedRoles={MANUALS[manual]?.roles || ["student", "instructor", "admin"]}>{children}</ProtectedRoute>;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [school, setSchool] = useState(null);

  const fetchSchool = async () => {
    try {
      const r = await axios.get(`${API}/auth/me/school`, { withCredentials: true });
      const s = r.data?.school || null;
      setSchool(s);
      // Apply CSS variable for primary color so any --school-primary user gets it
      if (s?.primary_color) {
        document.documentElement.style.setProperty("--school-primary", s.primary_color);
      } else {
        document.documentElement.style.removeProperty("--school-primary");
      }
    } catch (e) {
      // Non-fatal — fall back to default branding
      setSchool(null);
    }
  };

  const checkAuth = async () => {
    try {
      // The axios interceptor automatically adds Authorization header if token exists
      // This ensures both cookie and token-based auth work transparently
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      // Load school branding in parallel (best-effort)
      fetchSchool();
    } catch (error) {
      // Auth failed - clear any stale token
      if (error.response?.status === 401) {
        console.log('[Auth] Authentication failed, clearing localStorage token');
        try {
          localStorage.removeItem(SESSION_TOKEN_KEY);
        } catch (storageError) {
          // localStorage not available, ignore
        }
      }
      setUser(null);
      setSchool(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    setUser(userData);
    fetchSchool();
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Clear localStorage token (iOS Safari fallback)
    try {
      localStorage.removeItem(SESSION_TOKEN_KEY);
    } catch (storageError) {
      // localStorage not available, ignore
    }
    setUser(null);
    setSchool(null);
    document.documentElement.style.removeProperty("--school-primary");
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, school, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (from OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Public instructor status (QR-code) — no auth required */}
      <Route path="/di/status/:token" element={<PublicInstructorStatus />} />
      {/* Help manuals — registered users only; each manual is limited to the roles it is written for */}
      <Route path="/ajuda" element={
        <ProtectedRoute allowedRoles={["student", "instructor", "admin"]}>
          <HelpIndex />
        </ProtectedRoute>
      } />
      <Route path="/ajuda/:manual" element={
        <HelpGate>
          <HelpPage />
        </HelpGate>
      } />
      
      {/* Student Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute trainingOnly allowedRoles={["student", "instructor", "admin"]}>
          <StudentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/reservas" element={
        <ProtectedRoute allowedRoles={["student", "instructor", "admin", "super_admin"]}>
          <ReservationsPage />
        </ProtectedRoute>
      } />
      <Route path="/registar-voo" element={
        <ProtectedRoute allowedRoles={["student", "instructor", "admin", "super_admin"]}>
          <PilotFlightLogPage />
        </ProtectedRoute>
      } />
      <Route path="/meus-documentos" element={
        <ProtectedRoute allowedRoles={["student", "instructor", "admin", "super_admin"]}>
          <MeusDocumentosPage />
        </ProtectedRoute>
      } />
      <Route path="/training" element={
        <ProtectedRoute trainingOnly allowedRoles={["student", "instructor", "admin"]}>
          <TrainingPage />
        </ProtectedRoute>
      } />
      <Route path="/exam/:examId" element={
        <ProtectedRoute trainingOnly allowedRoles={["student", "instructor", "admin"]}>
          <ExamPage />
        </ProtectedRoute>
      } />
      <Route path="/exam/:examId/review" element={
        <ProtectedRoute trainingOnly allowedRoles={["student", "instructor", "admin"]}>
          <ExamReviewPage />
        </ProtectedRoute>
      } />
      <Route path="/exam/:examId/instructor-review" element={
        <ProtectedRoute trainingOnly allowedRoles={["instructor", "admin"]}>
          <InstructorExamReviewPage />
        </ProtectedRoute>
      } />
      <Route path="/badges" element={
        <ProtectedRoute trainingOnly allowedRoles={["student", "instructor", "admin"]}>
          <BadgesPage />
        </ProtectedRoute>
      } />
      <Route path="/missions" element={
        <ProtectedRoute trainingOnly allowedRoles={["student", "instructor", "admin"]}>
          <MissionsPage />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute trainingOnly allowedRoles={["student", "instructor", "admin"]}>
          <LeaderboardPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute trainingOnly allowedRoles={["student", "instructor", "admin"]}>
          <SubscriptionPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute trainingOnly allowedRoles={["student", "instructor", "admin"]}>
          <SubscriptionPage />
        </ProtectedRoute>
      } />
      
      {/* Instructor Routes */}
      <Route path="/instructor" element={
        <ProtectedRoute trainingOnly allowedRoles={["instructor", "admin", "super_admin"]}>
          <InstructorDashboard />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />

      {/* Super-Admin Routes */}
      <Route path="/super-admin/schools" element={
        <ProtectedRoute allowedRoles={["super_admin"]}>
          <SuperAdminSchools />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

import InstallPWABanner from "./components/InstallPWABanner";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#0f172a',
              border: '1px solid #334155',
              color: '#f8fafc',
            },
          }}
        />
        <AppRouter />
        <InstallPWABanner />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
